// Cuentas entre taquerías (administrador): deudas generadas por salidas de productos y sus pagos.
// Control financiero, no inventario. Respeta la "Sucursal actual" del panel (o todas).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useSucursalActiva } from '../../context/SucursalActivaContext';
import { getCuentasEntreSucursales, getCuentaEntreSucursales, listTaquerias, registrarPagoEntreSucursales, anularSalidaProductos } from '../../services/data';
import { toMoney } from '../../utils/numbers';
import { formatDateTime } from '../../utils/dates';
import { ComprobanteLink } from '../finanzas/Comprobante';
import SalidasPendientesValor, { TablaProductosSalida, ValorarSalidaModal } from './ValorPendiente';
import EditarSalidaModal from './EditarSalidaModal';
import MoneyInput from '../common/MoneyInput';
import '../finanzas/ui.css';
import '../proveedores/Proveedores.css';
import './CuentasEntreSucursales.css';
import FondoModal from '../common/FondoModal';

const fechaHora = formatDateTime;
const BADGE = { Pendiente: 'pendiente', 'Parcialmente pagado': 'parcial', Pagado: 'completado', Anulado: 'anulado' };
const Estado = ({ estado }) => <span className={`fx-badge fx-badge--${BADGE[estado] || 'pendiente'}`}>{estado}</span>;
const FILTROS = [['abiertas', 'Con saldo'], ['todas', 'Todas'], ['Pagado', 'Pagadas'], ['Anulado', 'Anuladas']];
/** Tabla compacta Sucursal | Pendiente con total (solo filas con saldo > 0). */
function TablaResumen({ titulo, filas }) {
  const visibles = filas.filter((f) => f.saldo > 0);
  return <section className="fx-card ces-mini"><h2>{titulo}</h2>
    {!visibles.length ? <p className="fx-empty">Sin saldos pendientes.</p> : <table className="fx-table">
      <thead><tr><th>Sucursal</th><th className="num">Pendiente</th></tr></thead>
      <tbody>{visibles.map((f) => <tr key={f.sucursalId}><td>{f.sucursal}</td><td className="num">{toMoney(f.saldo)}</td></tr>)}</tbody>
      <tfoot><tr><td><b>Total</b></td><td className="num"><b>{toMoney(visibles.reduce((t, f) => t + f.saldo, 0))}</b></td></tr></tfoot>
    </table>}
  </section>;
}
const claveNueva = () => `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

/** Registrar pago: paga el deudor y recibe el acreedor de la cuenta (no se eligen libremente). */
export function PagoModal({ cuenta, formas, onClose, onPagado }) {
  const [form, setForm] = useState({ monto: '', formaPago: 'Efectivo', referencia: '', observacion: '' });
  const [clave] = useState(claveNueva); // un envío = un pago, aunque se presione dos veces
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const monto = Number(form.monto) || 0;
  const guardar = async (e) => {
    e.preventDefault(); setError('');
    if (!/^\d+(?:\.\d{1,2})?$/.test(form.monto) || monto <= 0) return setError('Ingresa un monto mayor a 0.');
    if (monto > cuenta.saldo) return setError(`El pago no puede superar el saldo pendiente (${toMoney(cuenta.saldo)}).`);
    setBusy(true);
    try {
      const r = await registrarPagoEntreSucursales(cuenta.id, { ...form, clave });
      await Swal.fire({ icon: 'success', title: 'Pago registrado', timer: 1800, showConfirmButton: false,
        text: `Saldo pendiente: ${toMoney(r.saldo ?? cuenta.saldo - monto)}${form.formaPago === 'Efectivo' && r.cajaRecibe === false ? ` · ${cuenta.acreedor} no tenía caja abierta: el efectivo no se sumó a ninguna caja.` : ''}` });
      onPagado();
    } catch (x) { setError(x.message || 'No se pudo registrar el pago'); }
    finally { setBusy(false); }
  };
  return (
    <FondoModal onClose={onClose} style={{ zIndex: 1300 }}>
      <form className="fx-modal fx-modal--sm fx-compacto" role="dialog" aria-modal="true" aria-label="Registrar pago" onMouseDown={(e) => e.stopPropagation()} onSubmit={guardar}>
        <header><div><h2>Registrar pago</h2><p>{cuenta.deudor} paga a {cuenta.acreedor}</p></div><button type="button" aria-label="Cerrar" onClick={onClose}>×</button></header>
        <p className="fx-intro">Completa la información. Los campos marcados con * son obligatorios.</p>
        <dl className="fx-resumen">
          <div><dt>Valor original</dt><dd>{toMoney(cuenta.total)}</dd></div>
          <div><dt>Pagado</dt><dd>{toMoney(cuenta.pagado)}</dd></div>
          <div><dt>Saldo pendiente</dt><dd>{toMoney(cuenta.saldo)}</dd></div>
        </dl>
        <div className="fx-form">
          {/* Informativos: los define la cuenta (paga el deudor, recibe el acreedor). */}
          <label>Sucursal que paga<input className="fx-solo-lectura" value={cuenta.deudor} readOnly tabIndex={-1} /></label>
          <label>Sucursal que recibe<input className="fx-solo-lectura" value={cuenta.acreedor} readOnly tabIndex={-1} /></label>
          <label>Monto pagado <span className="fx-req" aria-hidden="true">*</span><MoneyInput autoFocus value={form.monto} onChange={(v) => setForm((f) => ({ ...f, monto: v }))} /></label>
          <label>Forma de pago <span className="fx-req" aria-hidden="true">*</span><select value={form.formaPago} onChange={(e) => setForm((f) => ({ ...f, formaPago: e.target.value }))}>
            {formas.map((x) => <option key={x}>{x}</option>)}</select></label>
          <label>Referencia<input maxLength={120} value={form.referencia} placeholder="Ej.: transferencia 12345" onChange={(e) => setForm((f) => ({ ...f, referencia: e.target.value }))} /></label>
          {/* Derivado: se recalcula al escribir el monto; no es editable. */}
          <div className="fx-campo"><span className="fx-campo-titulo">Saldo después del pago</span>
            <output className="fx-valor" aria-live="polite">{toMoney(Math.max(0, cuenta.saldo - monto))}</output></div>
          <label className="full">Observación<textarea rows={2} maxLength={500} value={form.observacion} onChange={(e) => setForm((f) => ({ ...f, observacion: e.target.value }))} /></label>
        </div>
        <p className="fx-nota"><span aria-hidden="true">ℹ</span>{form.formaPago === 'Efectivo'
          ? `Sale de la caja abierta de ${cuenta.deudor} y entra a la caja abierta de ${cuenta.acreedor} (si la tiene).`
          : 'No mueve efectivo de ninguna caja; solo abona a la deuda.'}</p>
        {error && <p role="alert" className="ces-error">{error}</p>}
        <div className="fx-actions">
          <button type="button" className="fx-btn" onClick={onClose}>Cancelar</button>
          <button className="fx-btn fx-btn--primary" disabled={busy}>{busy ? 'Guardando…' : 'Registrar pago'}</button>
        </div>
      </form>
    </FondoModal>
  );
}

function DetalleModal({ id, formas, onClose, onCambio }) {
  const [d, setD] = useState(null);
  const [pagando, setPagando] = useState(false);
  const [valorando, setValorando] = useState(false);
  const [editando, setEditando] = useState(false);
  const [taquerias, setTaquerias] = useState([]);
  useEffect(() => { listTaquerias().then(setTaquerias).catch(() => setTaquerias([])); }, []);
  const cargar = useCallback(() => getCuentaEntreSucursales(id).then(setD).catch((e) => Swal.fire('Error', e.message || '', 'error')), [id]);
  useEffect(() => { cargar(); }, [cargar]);
  const anular = async () => {
    const { value: motivo } = await Swal.fire({ title: 'Anular salida de productos', input: 'textarea', inputLabel: 'Motivo de la anulación',
      text: `La deuda de ${d.cuenta.deudor} por ${toMoney(d.cuenta.total)} quedará anulada.`, showCancelButton: true,
      confirmButtonText: 'Anular salida', cancelButtonText: 'Cancelar', inputValidator: (v) => (!v?.trim() ? 'Indica el motivo' : undefined) });
    if (!motivo) return;
    try { await anularSalidaProductos(d.salida.id, motivo.trim()); await cargar(); onCambio(); }
    catch (e) { Swal.fire('No se pudo anular', e.message || '', 'error'); }
  };
  const c = d?.cuenta, s = d?.salida;
  return (
    <FondoModal onClose={onClose}>
      <section className="fx-modal" role="dialog" aria-modal="true" aria-label="Detalle de cuenta" onMouseDown={(e) => e.stopPropagation()}>
        <header><div><h2>{c ? `${c.deudor} debe a ${c.acreedor}` : 'Cuenta entre taquerías'}</h2>
          {c && <p>Deuda generada por una entrega de productos</p>}</div>
          <button type="button" aria-label="Cerrar" onClick={onClose}>×</button></header>
        {!d ? <p className="fx-empty">Cargando…</p> : <>
          <div className="fx-grid">
            <div className="fx-card fx-stat"><span>Valor original</span><b>{toMoney(c.total)}</b></div>
            <div className="fx-card fx-stat"><span>Total pagado</span><b>{toMoney(c.pagado)}</b></div>
            <div className="fx-card fx-stat"><span>Saldo pendiente</span><b>{toMoney(c.saldo)}</b></div>
            <div className="fx-card fx-stat"><span>Estado</span><b><Estado estado={c.estado} /></b></div>
          </div>
          <h3 className="ces-h3">Salida de productos que originó la deuda</h3>
          {/* Detalle completo de lo entregado, tal como quedó registrado (con las correcciones, si las hubo). */}
          <dl className="ces-quien">
            <div><dt>Origen</dt><dd>{s.origen}</dd></div>
            <div><dt>Destino</dt><dd>{s.destino}</dd></div>
            <div><dt>Entregó</dt><dd>{s.entregadoPor || 'Sin registrar'}</dd></div>
            <div><dt>Recibió</dt><dd>{s.recibidoPor || 'Sin registrar'}</dd></div>
            <div><dt>Recibo firmado</dt><dd>{s.reciboUrl ? <ComprobanteLink url={s.reciboUrl} texto="Ver recibo firmado" /> : 'Sin comprobante'}</dd></div>
            <div><dt>Fecha</dt><dd className="ces-nowrap">{fechaHora(s.fechaAt)}</dd></div>
            <div><dt>Registró</dt><dd>{s.usuario || '—'}</dd></div>
          </dl>
          {(s.observacion || s.estado === 'Anulada') && <p className="ces-sub">{[s.observacion && `Observación: ${s.observacion}`,
            s.estado === 'Anulada' && `Anulada por ${s.anuladoPor} (${fechaHora(s.anuladoAt)}): ${s.anuladoMotivo}`].filter(Boolean).join(' · ')}</p>}
          <TablaProductosSalida salida={s} />
          {s.valorPendiente && s.estado !== 'Anulada' && <p className="ces-nota">La deuda solo incluye lo valorado. <button type="button" className="fx-link" onClick={() => setValorando(true)}>Completar valor</button></p>}
          {(d.correcciones || []).length > 0 && <details className="es-historial">
            <summary>Historial de correcciones ({d.correcciones.length})</summary>
            <ul>{d.correcciones.map((x) => <li key={x.id}><b>{x.usuario}</b> · {fechaHora(x.fechaAt)} — {x.motivo}
              <ul>{(x.cambios || []).map((k, i) => <li key={i}>{k.campo}: {k.antes ?? '—'} → {k.despues ?? '—'}</li>)}</ul></li>)}</ul>
            {d.original && <p className="ces-sub">Registro original del cajero: {d.original.items.map((i) => `${i.producto} ${i.cantidad}${i.total != null ? ` · ${toMoney(i.total)}` : ' · valor pendiente'}`).join('; ')} · total {toMoney(d.original.total)}</p>}
          </details>}
          <h3 className="ces-h3">Pagos realizados</h3>
          {!d.pagos.length ? <p className="fx-empty">Sin pagos registrados.</p> : <div className="ces-scroll"><table className="fx-table">
            <thead><tr><th>Fecha</th><th>Paga</th><th>Recibe</th><th>Forma de pago</th><th>Usuario</th><th>Detalle</th><th className="num">Monto</th></tr></thead>
            <tbody>{d.pagos.map((p) => <tr key={p.id}><td className="ces-nowrap">{fechaHora(p.fechaAt)}</td><td>{p.paga}</td><td>{p.recibe}</td><td>{p.formaPago}</td><td>{p.usuario}</td>
              <td>{[p.referencia && `Ref. ${p.referencia}`, p.observacion, p.formaPago === 'Efectivo' && (p.cajaRecibe ? 'entró a caja' : 'sin caja receptora')].filter(Boolean).join(' · ') || '—'}</td>
              <td className="num">{toMoney(p.monto)}</td></tr>)}</tbody>
          </table></div>}
          <div className="fx-actions">
            {c.estado !== 'Anulado' && s.estado !== 'Anulada' && <button type="button" className="fx-btn" onClick={() => setEditando(true)}>Editar</button>}
            {c.estado !== 'Anulado' && c.pagado <= 0 && <button type="button" className="fx-btn fx-btn--danger" onClick={anular}>Anular salida</button>}
            {c.estado !== 'Anulado' && c.saldo > 0 && <button type="button" className="fx-btn fx-btn--primary" onClick={() => setPagando(true)}>Registrar pago</button>}
          </div>
          {c.estado !== 'Anulado' && c.pagado > 0 && <p className="ces-nota">La salida tiene pagos registrados: no se puede anular; al corregirla los pagos se conservan y el valor no puede quedar por debajo de lo pagado.</p>}
        </>}
        {pagando && <PagoModal cuenta={c} formas={formas} onClose={() => setPagando(false)} onPagado={() => { setPagando(false); cargar(); onCambio(); }} />}
        {valorando && <ValorarSalidaModal salida={s} onClose={() => setValorando(false)} onSaved={() => { setValorando(false); cargar(); onCambio(); }} />}
        {editando && <EditarSalidaModal salida={s} taquerias={taquerias} pagado={c.pagado} onClose={() => setEditando(false)}
          onSaved={async () => { setEditando(false); await cargar(); onCambio(); }} />}
      </section>
    </FondoModal>
  );
}

export default function CuentasEntreSucursales() {
  const { sucursalId, nombreSucursal, todas } = useSucursalActiva();
  const [data, setData] = useState(null);
  const [filtro, setFiltro] = useState('abiertas');
  const [buscar, setBuscar] = useState('');
  const [detalle, setDetalle] = useState(null);
  const [pago, setPago] = useState(null);
  const cargar = useCallback(() => {
    if (!sucursalId) return;
    getCuentasEntreSucursales(sucursalId).then(setData).catch((e) => Swal.fire('Error', e.message || 'No se pudieron cargar las cuentas.', 'error'));
  }, [sucursalId]);
  useEffect(() => { cargar(); }, [cargar]);

  const cuentas = useMemo(() => (data?.cuentas || []).filter((c) => (filtro === 'todas' ? true : filtro === 'abiertas' ? c.estado !== 'Anulado' && c.saldo > 0 : c.estado === filtro))
    .filter((c) => !buscar.trim() || `${c.acreedor} ${c.deudor} ${c.observacion || ''}`.toLowerCase().includes(buscar.trim().toLowerCase())), [data, filtro, buscar]);
  // Agrupación por sucursal (saldo pendiente real = original − pagado; el servidor ya excluye las anuladas).
  // Sucursal elegida: su resumen trae a cada contraparte. Todas: total por cobrar / por pagar de cada sucursal.
  const resumen = data?.resumen || [];
  const propio = resumen.find((r) => r.sucursalId === sucursalId);
  const meDeben = todas ? resumen.map((r) => ({ sucursalId: r.sucursalId, sucursal: r.sucursal, saldo: r.porCobrar.saldo })) : propio?.porCobrar.detalle || [];
  const debo = todas ? resumen.map((r) => ({ sucursalId: r.sucursalId, sucursal: r.sucursal, saldo: r.porPagar.saldo })) : propio?.porPagar.detalle || [];
  const formas = data?.formasPago || ['Efectivo', 'Transferencia', 'Otro'];

  return <div className="prov-shell ces-shell">
    <header className="prov-header"><div><h1>Cuentas entre taquerías</h1>
      <p className="prov-sub">Deudas generadas por salidas de productos entre sucursales y sus pagos · {nombreSucursal || 'Todas las sucursales'}</p></div></header>

    {data && <div className="ces-resumen">
      <TablaResumen titulo={todas ? 'Por cobrar (le deben)' : 'Me deben'} filas={meDeben} />
      <TablaResumen titulo={todas ? 'Por pagar (debe)' : 'Debo'} filas={debo} />
    </div>}

    <SalidasPendientesValor key={`${sucursalId}-${data ? data.cuentas.length : 0}`} sucursalId={sucursalId} onCambio={cargar} />

    <section className="fx-card">
      <div className="prov-filtros">
        <input type="search" placeholder="Buscar sucursal u observación" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
        <label>Estado<select value={filtro} onChange={(e) => setFiltro(e.target.value)}>{FILTROS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
      </div>
      {!data ? <p className="fx-empty">Cargando…</p> : !cuentas.length ? <p className="fx-empty">Sin cuentas para mostrar.</p> : <div className="ces-scroll"><table className="fx-table">
        <thead><tr><th>Fecha</th><th>Debe</th><th>Le debe a</th>{!todas && <th>Para {nombreSucursal}</th>}<th className="num">Original</th><th className="num">Pagado</th><th className="num">Saldo</th><th>Estado</th><th>Acciones</th></tr></thead>
        {/* Se lee de corrido: "Que Taco 2 debe Q50.00 a Los Esquite". */}
        <tbody>{cuentas.map((c) => <tr key={c.id}><td>{fechaHora(c.fechaAt)}</td><td>{c.deudor}</td><td>{c.acreedor}</td>
          {!todas && <td>{c.acreedorId === sucursalId ? 'Por cobrar' : 'Por pagar'}</td>}
          <td className="num">{toMoney(c.total)}</td><td className="num">{toMoney(c.pagado)}</td><td className="num"><b>{toMoney(c.saldo)}</b></td><td><Estado estado={c.estado} />{c.valorPendiente && c.estado !== 'Anulado' && <span className="fx-badge fx-badge--hoy" style={{ marginLeft: 4 }} title="Hay productos sin valor: la deuda solo incluye lo valorado">Valor pendiente</span>}</td>
          <td><span className="ces-acciones"><button className="fx-btn" onClick={() => setDetalle(c.id)}>Ver detalle</button>
            {c.estado !== 'Anulado' && c.saldo > 0 && <button className="fx-btn fx-btn--primary" onClick={() => setPago(c)}>Registrar pago</button>}</span></td></tr>)}</tbody>
      </table></div>}
    </section>
    {detalle && <DetalleModal id={detalle} formas={formas} onClose={() => setDetalle(null)} onCambio={cargar} />}
    {pago && <PagoModal cuenta={pago} formas={formas} onClose={() => setPago(null)} onPagado={() => { setPago(null); cargar(); }} />}
  </div>;
}
